import { useForBinding } from '@practium/features/forBinding';
import { usePermissions } from '@practium/features/permissions';
import type { CorrectionStartResponse } from '@practium/features/recommendations';
import { CorrectionWorkspacePage } from '@practium/features/recommendations';
import { LoadingState, useToast } from '@practium/ui';
import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { getRecommendationsListPath } from '../routes';
import {
	useDeleteAdviceCorrection,
	useGetAdviceCorrectionById,
	usePostAdviceCorrectionStart,
} from '@practium/features/api';

const getIdValue = (value: unknown): string => {
	if (!value) return '';
	if (typeof value === 'string') return value;
	if (typeof value === 'object') {
		const record = value as Record<string, unknown>;
		const idValue = record._id ?? record.id;
		return typeof idValue === 'string' ? idValue : '';
	}
	return String(value);
};

export const ViewCorrectionAdvicePageClient = () => {
	const navigate = useNavigate();
	const location = useLocation();
	const toast = useToast();
	const hasStartedRef = useRef(false);
	const [viewStartResult, setViewStartResult] = useState<CorrectionStartResponse | undefined>();
	const [searchParams] = useSearchParams();
	const id = searchParams.get('id') || '';
	const { isForBinding } = useForBinding();
	const { abilities } = usePermissions();
	const subject = isForBinding ? 'farmers-recommendations' : 'recommendations';
	const { data: advice, isLoading } = useGetAdviceCorrectionById(id);
	const { mutateAsync: deleteCorrectionAdvice } = useDeleteAdviceCorrection();
	const { mutateAsync: startCorrectionAdvice, isPending: isStartingAdvice } =
		usePostAdviceCorrectionStart();
	const startResult = (location.state as { correctionStart?: CorrectionStartResponse } | null)
		?.correctionStart;
	const effectiveStartResult = startResult ?? viewStartResult;

	const canEdit = abilities.can('update', subject);

	const canDelete = abilities.can('delete', subject);

	useEffect(() => {
		if (!advice || !id || startResult || hasStartedRef.current) return;

		hasStartedRef.current = true;
		startCorrectionAdvice({
			adviceId: id,
			prop: getIdValue(advice.prop),
			area: getIdValue(advice.area),
			sectors: advice.sectors.map(getIdValue).filter(Boolean),
			soilAnalysis: getIdValue(advice.soilAnalysis),
		})
			.then(setViewStartResult)
			.catch(() => {
				toast.error('Erro ao carregar situação atual e faixa adequada.');
			});
	}, [advice, id, startCorrectionAdvice, startResult, toast]);

	if (!advice || isLoading || (!effectiveStartResult && isStartingAdvice)) {
		return <LoadingState />;
	}

	const handleDelete = async () => {
		try {
			await deleteCorrectionAdvice(id);
			navigate(getRecommendationsListPath('correction'));
		} catch (error: any) {
			toast.error(error.message);
		}
	};

	const handleEdit = () => navigate(`/recomendacoes/correcao/editar?id=${id}`);

	return (
		<CorrectionWorkspacePage
			adviceId={id}
			viewData={advice}
			onBack={() => navigate(getRecommendationsListPath('correction'))}
			onEdit={canEdit ? handleEdit : undefined}
			onDelete={canDelete ? handleDelete : undefined}
			canEditTabs={canEdit}
			startResult={effectiveStartResult}
		/>
	);
};
